import { PUBLIC_INGRESS_IP } from '../../config/apiBase'
import { useLang } from '../../i18n/LanguageContext'

// Where a customer's own DNS should point. The CNAME target is the tenant's
// platform subdomain; the A record is the public ingress IP. If the ingress IP
// ever changes, existing customers' A records break — one more reason the
// instructions call CNAME "easiest".

/**
 * Deliberately does NOT say "everything is ready": the tenant is created but a
 * portal admin still finalizes the site (content check, DNS verification)
 * before it should be shared. The customer waits for the "you are live" email
 * — TenantReadyEvent on the backend — instead of burning their launch moment
 * on a half-configured address.
 */
export default function StepSuccess({ result, data }) {
  const { t } = useLang()

  const tenantName = result?.tenantName || data?.tenantName || ''
  const slug = result?.tenantSlug || data?.slug || ''
  const username = result?.username || data?.ownerUsername || ''
  const contactEmail = data?.contactEmail || ''
  const customDomain = data?.customDomain?.trim() || ''
  const subdomainHost = `${slug}.communitynord.com`

  return (
    <div className="wizard__body">
      <div className="success">
        <div className="success__icon">
          <svg width="32" height="32" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>

        <h1 className="success__title">
          {t('onboarding.successTitle', { name: tenantName })}
        </h1>

        <p className="success__message">
          {contactEmail
            ? t('onboarding.successAwaiting', { email: contactEmail })
            : t('onboarding.successAwaitingNoEmail')}
        </p>

        <div className="success-next">
          <div className="success-next__title">{t('onboarding.successWhatNow')}</div>
          <ol>
            <li>{t('onboarding.successStep1')}</li>
            <li>{t('onboarding.successStep2')}</li>
            <li>{t('onboarding.successStep3', { username })}</li>
          </ol>
        </div>

        <div className="review__section" style={{ textAlign: 'left', marginBottom: '2rem' }}>
          <div className="review__heading">{t('onboarding.successDetails')}</div>
          <div className="review__grid">
            <span className="review__label">{t('onboarding.successOrg')}</span>
            <span className="review__value">{tenantName}</span>

            <span className="review__label">{t('onboarding.successUrl')}</span>
            <span className="review__value">{subdomainHost}</span>

            <span className="review__label">{t('onboarding.successUsername')}</span>
            <span className="review__value">{username}</span>
          </div>
        </div>

        {customDomain && (
          <div className="dns-box">
            <div className="dns-box__title">
              {t('onboarding.successDnsTitle', { domain: customDomain })}
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
              {t('onboarding.successDnsIntro', { domain: customDomain })}
            </p>

            <div className="dns-box__option">
              <div className="dns-box__option-title">{t('onboarding.successDnsCnameTitle')}</div>
              <div>{t('onboarding.successDnsCnameText')}</div>
              <table>
                <thead>
                  <tr>
                    <th>{t('onboarding.successDnsType')}</th>
                    <th>{t('onboarding.successDnsName')}</th>
                    <th>{t('onboarding.successDnsValue')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>CNAME</code></td>
                    <td><code>{customDomain}</code></td>
                    <td><code>{subdomainHost}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="dns-box__option">
              <div className="dns-box__option-title">{t('onboarding.successDnsATitle')}</div>
              <div>{t('onboarding.successDnsAText')}</div>
              <table>
                <thead>
                  <tr>
                    <th>{t('onboarding.successDnsType')}</th>
                    <th>{t('onboarding.successDnsName')}</th>
                    <th>{t('onboarding.successDnsValue')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>A</code></td>
                    <td><code>{customDomain}</code></td>
                    <td><code>{PUBLIC_INGRESS_IP}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="dns-box__note">
              {t('onboarding.successDnsNote')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
